import React, { useEffect, useState } from 'react';
import { connector } from '../../../services/api';
import type { WorkflowRollbackResult, WorkflowVersionInfo } from '../../../types/workflow';
import { describeWorkflowError, shortDigest, type WorkflowErrorInfo } from '../../../utils/workflowDsl';
import { Banner, Pill } from './WorkflowFormControls';
import WorkflowModal from './WorkflowModal';

export interface WorkflowRollbackModalProps {
  open: boolean;
  integrationId: string;
  versions: WorkflowVersionInfo[];
  currentVersionId: string | null | undefined;
  initialTarget: string | null;
  onClose: () => void;
  /** Called after a real rollback succeeded — the parent reloads the snapshot. */
  onApplied: (result: WorkflowRollbackResult) => void;
}

const WorkflowRollbackModal: React.FC<WorkflowRollbackModalProps> = ({ open, integrationId, versions, currentVersionId, initialTarget, onClose, onApplied }) => {
  const [target, setTarget] = useState<string>('');
  const [comment, setComment] = useState('');
  const [preview, setPreview] = useState<WorkflowRollbackResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<WorkflowErrorInfo | null>(null);

  useEffect(() => {
    if (!open) return;
    setTarget(initialTarget || '');
    setComment('');
    setPreview(null);
    setError(null);
    setPreviewing(false);
    setApplying(false);
  }, [open, initialTarget]);

  const sorted = [...versions].sort((a, b) => b.version - a.version);
  const targetInfo = sorted.find((v) => v.version_id === target) || null;

  const runPreview = async () => {
    if (!target) return;
    setPreviewing(true);
    setError(null);
    setPreview(null);
    try {
      const res = await connector.rollbackWorkflow(integrationId, { to_workflow_version: target, dry_run: true, comment: comment || undefined });
      setPreview(res);
    } catch (err) {
      setError(describeWorkflowError(err, 'Rollback preview failed.'));
    } finally {
      setPreviewing(false);
    }
  };

  const runApply = async () => {
    if (!target) return;
    setApplying(true);
    setError(null);
    try {
      const res = await connector.rollbackWorkflow(integrationId, { to_workflow_version: target, dry_run: false, comment: comment || undefined });
      onApplied(res);
    } catch (err) {
      setError(describeWorkflowError(err, 'Rollback failed.'));
    } finally {
      setApplying(false);
    }
  };

  const busy = previewing || applying;

  return (
    <WorkflowModal
      open={open}
      title="Roll back workflow"
      icon="fa-history"
      onClose={busy ? () => undefined : onClose}
      footer={
        <>
          <button type="button" className="cwf-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="cwf-btn" onClick={runPreview} disabled={!target || busy}>
            {previewing ? <i className="fas fa-spinner fa-spin" aria-hidden="true" /> : <i className="fas fa-search" aria-hidden="true" />} Preview (dry run)
          </button>
          <button
            type="button"
            className="cwf-btn cwf-btn--danger-solid"
            onClick={runApply}
            disabled={!target || busy || target === currentVersionId || (preview != null && preview.would_change === false)}
            title={preview != null && preview.would_change === false ? 'The preview reports no change' : undefined}
          >
            {applying ? <i className="fas fa-spinner fa-spin" aria-hidden="true" /> : <i className="fas fa-undo" aria-hidden="true" />} Roll back
          </button>
        </>
      }
    >
      <p className="cwf-note">
        The target is a <code>version_id</code> from <code>workflow_versions</code>. Preview first: a dry run reports what would change without writing anything.
        Component libraries are append-only and are not touched by a rollback.
      </p>

      <label className="cwf-field">
        <span className="cwf-field__label">Target version</span>
        <select
          className="cwf-input"
          value={target}
          disabled={busy}
          onChange={(e) => {
            setTarget(e.target.value);
            setPreview(null);
            setError(null);
          }}
        >
          <option value="">— select a version —</option>
          {sorted.map((v) => (
            <option key={v.version_id} value={v.version_id} disabled={v.version_id === currentVersionId}>
              v{v.version} · {v.status} · {v.name}
              {v.version_id === currentVersionId ? ' (current)' : ''}
            </option>
          ))}
        </select>
      </label>
      {targetInfo ? (
        <div className="cwf-muted">
          version_id <code>{targetInfo.version_id}</code>
        </div>
      ) : null}

      <label className="cwf-field">
        <span className="cwf-field__label">Comment (goes to the audit log)</span>
        <input className="cwf-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Why roll back?" disabled={busy} />
      </label>

      {error ? (
        <Banner tone="error" title={`Rollback ${error.status ? `failed (${error.status})` : 'failed'}`} details={error.details}>
          <p>{error.message}</p>
        </Banner>
      ) : null}

      {preview ? (
        <div className="cwf-plan">
          <div className="cwf-plan__summary">
            <Pill tone={preview.would_change ? 'accent' : 'neutral'}>{preview.would_change ? 'would change' : 'no change'}</Pill>
            <span className="cwf-muted">
              current_revision <code title={preview.current_revision}>{shortDigest(preview.current_revision)}</code>
            </span>
          </div>
          <dl className="cwf-dl">
            <dt>from</dt>
            <dd>
              v{preview.from_workflow_version} · <code>{preview.from_workflow_version_id}</code>
            </dd>
            <dt>to</dt>
            <dd>
              v{preview.to_workflow_version} · <code>{preview.to_workflow_version_id}</code>
            </dd>
          </dl>
        </div>
      ) : null}
    </WorkflowModal>
  );
};

export default WorkflowRollbackModal;
